import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Lock, Clock } from "lucide-react";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { NAV_GROUPS } from "../../lib/sidebarGroups";
import api from "../../lib/api";

const STORAGE_KEY = "mg_sidebar_groups_state_v1";

function loadGroupState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
}
function saveGroupState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage blocked */ }
}

/**
 * Sidebar agrupado (Entrega 1 del refactor UX).
 *
 * Props:
 *   role: rol del usuario en el restaurante actual ("owner"|"manager"|...).
 *   restaurantId: para fetch de enabled_modules.
 *   collapsed: bool, si la sidebar está colapsada (solo iconos).
 *   isStarter: bool, plan Starter (oculta items bloqueados en lugar de mostrar lock).
 *   hasModule: function(moduleKey)→bool del usePlanFeatures().
 *   badges: { draftOrders, fiscalUnresolved } para mostrar pills numéricas.
 */
export default function SidebarNav({
  role, restaurantId, collapsed, isStarter, hasModule, badges = {}, onItemClick,
}) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(loadGroupState);
  const [enabledModules, setEnabledModules] = useState(null); // null = loading

  // Fetch enabled_modules for this restaurant
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    api.request(`/restaurants/${restaurantId}/sidebar-modules`)
      .then((r) => { if (!cancelled) setEnabledModules(r?.enabled_modules || []); })
      .catch(() => { if (!cancelled) setEnabledModules([]); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  // Initialize each group as open if not yet stored
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      NAV_GROUPS.forEach((g) => {
        if (typeof next[g.key] === "undefined") {
          next[g.key] = true; // open by default
          changed = true;
        }
      });
      if (changed) saveGroupState(next);
      return next;
    });
  }, []);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveGroupState(next);
      return next;
    });
  };

  const isActive = (path, exact) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const enabledSet = useMemo(
    () => new Set(enabledModules || []),
    [enabledModules],
  );

  // Filter visible groups: pinned (home/admin) always visible; others gated by enabled_modules.
  // While modules are loading we render a minimal skeleton (pinned only).
  const visibleGroups = useMemo(() => {
    return NAV_GROUPS
      .map((g) => {
        const items = (g.items || [])
          .filter((it) => it.roles.includes(role))
          .filter((it) => !isStarter || hasModule(it.moduleKey) || it.comingSoon);
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0)
      .filter((g) => {
        if (g.pinned === "top" || g.pinned === "bottom") return true;
        if (enabledModules == null) return false; // hide non-pinned during load
        return enabledSet.has(g.key);
      });
  }, [role, isStarter, hasModule, enabledModules, enabledSet]);

  const renderItem = (item) => {
    const active = isActive(item.path, item.exact);
    const Icon = item.icon;
    const planLocked = !isStarter && !hasModule(item.moduleKey);
    const disabled = item.comingSoon || planLocked;
    const badgeNum = item.path === "/app/inventory" ? badges.draftOrders
      : item.path === "/app/orders" ? badges.draftOrders
      : item.path === "/app/shopping-lists" ? badges.shoppingListsPending
      : item.path === "/app/finance" ? badges.fiscalUnresolved
      : 0;

    const inner = (
      <span className={`flex items-center gap-3 w-full ${collapsed ? "justify-center" : ""}`}>
        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-primary" : ""}`} />
        {!collapsed && (
          <>
            <span className={`text-sm flex-1 truncate ${active ? "font-medium" : ""}`}>
              {item.label}
            </span>
            {item.comingSoon && (
              <Clock className="w-3 h-3 text-muted-foreground/60" />
            )}
            {planLocked && !item.comingSoon && (
              <Lock className="w-3 h-3 text-muted-foreground/60" />
            )}
            {badgeNum > 0 && !disabled && (
              <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0 h-4">
                {badgeNum > 9 ? "9+" : badgeNum}
              </Badge>
            )}
          </>
        )}
      </span>
    );

    const baseCls = `block rounded-md px-2.5 py-2 transition-colors ${
      active
        ? "bg-primary/10 text-primary"
        : disabled
          ? "text-muted-foreground/50 cursor-not-allowed"
          : "text-foreground hover:bg-muted/60 hover:text-foreground"
    }`;

    const tooltipText = item.comingSoon
      ? "Disponible próximamente"
      : planLocked
        ? "Disponible en planes superiores"
        : item.label;

    // Rich tooltip (label + description) for enabled items. Used on desktop
    // only; mobile bottom nav doesn't render this component.
    const enabledTooltip = !disabled && item.description ? (
      <div className="max-w-[240px]">
        <div className="font-medium text-xs mb-0.5">{item.label}</div>
        <div className="text-[11px] text-primary-foreground/80 leading-snug">{item.description}</div>
      </div>
    ) : null;

    if (disabled) {
      return (
        <TooltipProvider key={item.path} delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={baseCls}
                aria-disabled="true"
                data-testid={`sidebar-item-${item.path.replace(/\//g, "-")}`}
              >
                {inner}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">{tooltipText}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    const linkEl = (
      <Link
        key={item.path}
        to={item.path}
        onClick={onItemClick}
        className={baseCls}
        data-testid={`sidebar-item-${item.path.replace(/\//g, "-")}`}
        title={collapsed && !enabledTooltip ? item.label : undefined}
      >
        {inner}
      </Link>
    );

    if (!enabledTooltip) return linkEl;

    return (
      <TooltipProvider key={item.path} delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
          <TooltipContent side="right" className="p-2">{enabledTooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderGroup = (group) => {
    const GroupIcon = group.icon;
    const isOpen = openGroups[group.key] !== false;
    const isPinnedTop = group.pinned === "top";
    const isPinnedBottom = group.pinned === "bottom";
    // Pinned-top "Inicio" group has a single Dashboard link → no header,
    // would be visual noise. Pinned-bottom "Configuración" has 5 items →
    // render the same collapsible header pattern as middle groups so
    // the user can fold its sub-pages out of sight.
    const showHeader = !isPinnedTop;

    if (collapsed) {
      // Collapsed sidebar: render items flat without group headers
      return (
        <div key={group.key} className="space-y-0.5">
          {group.items.map(renderItem)}
          <div className="my-2 mx-2 border-t border-dashed border-border/40" />
        </div>
      );
    }

    return (
      <div key={group.key} data-testid={`sidebar-group-${group.key}`}>
        {showHeader && (
          group.description ? (
            <TooltipProvider delayDuration={400}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center gap-2 px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`sidebar-group-toggle-${group.key}`}
                  >
                    <GroupIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="flex-1 text-left">{group.label}</span>
                    <ChevronRight
                      className={`w-3 h-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[260px] p-2">
                  <div className="font-medium text-xs mb-0.5">{group.label}</div>
                  <div className="text-[11px] text-primary-foreground/80 leading-snug">
                    {group.description}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              type="button"
              onClick={() => toggleGroup(group.key)}
              className="w-full flex items-center gap-2 px-2 py-1 mt-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`sidebar-group-toggle-${group.key}`}
            >
              <GroupIcon className="w-3 h-3 flex-shrink-0" />
              <span className="flex-1 text-left">{group.label}</span>
              <ChevronRight
                className={`w-3 h-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
              />
            </button>
          )
        )}
        {(isOpen || !showHeader) && (
          <div className={`space-y-0.5 ${showHeader ? "ml-1" : ""}`}>
            {group.items.map(renderItem)}
          </div>
        )}
      </div>
    );
  };

  // Split: top-pinned, regular, bottom-pinned
  const topPinned = visibleGroups.filter((g) => g.pinned === "top");
  const middle = visibleGroups.filter((g) => !g.pinned);
  const bottomPinned = visibleGroups.filter((g) => g.pinned === "bottom");

  return (
    <nav className="space-y-1">
      {topPinned.map(renderGroup)}
      {topPinned.length > 0 && middle.length > 0 && (
        <div className="my-2 mx-1 border-t border-dashed border-border/50" />
      )}
      {middle.map(renderGroup)}
      {bottomPinned.length > 0 && (middle.length > 0 || topPinned.length > 0) && (
        <div className="my-3 mx-1 border-t border-border/50" />
      )}
      {bottomPinned.map(renderGroup)}
    </nav>
  );
}
